import { FC, ReactElement, ReactNode } from 'react';

export type Combine<A, B> = Partial<Omit<A, keyof B>> & // items in A not B
  Partial<Omit<B, keyof A>> & { [K in keyof A & keyof B]: A[K] | B[K] }; // items in B not A // combination of both

// Data leaves
export type AtomicEntry = string | number | boolean | null; // output
export type AtomicArray = string[] | number[] | boolean[] | null[]; // map to AtomicEntry

// Intermediate type
export type AtomicRecord = Record<string, AtomicEntry | AtomicArray>; // Object.entries, treat as above

// Data nodes
export type DecomposableRecord = Record<string, AtomicEntry | AtomicArray | AtomicRecord>; // Ojbect.entries, treat as above

export type DecomposableArray = Array<AtomicRecord | DecomposableRecord>; // map to AtomicRecord or DecomposableRecord

export type PossibleNodeTypes =
  | AtomicEntry
  | AtomicArray
  | AtomicRecord
  | DecomposableRecord
  | DecomposableArray;

export type NodeTrees = Record<string, PossibleNodeTypes>;

export type NestedTableNodeTree = {
  id: number | string;
  row: NonNullable<ReactNode>;
  nestedData?: NestedTableNodeTree[];
};

export enum ViewTypes {
  raw = 'raw',
  table = 'table',
  nestedTable = 'nested-table',
}

export enum NodeType {
  array = 'array',
  object = 'object',
  number = 'number',
  boolean = 'boolean',
  string = 'string',
  null = 'null',
}

// Tree components (i.e. root, branch, leaf) variant component props
export type RootProps = {
  children: ReactNode;
};

export type JsonBranchProps = {
  CollapseButton: FC<React.PropsWithChildren<unknown>>;
  depth?: number;
  inArray?: boolean;
  isCollapsed: boolean;
  isLast?: boolean;
  label: ReactNode;
  type: NodeType;
  value: Exclude<PossibleNodeTypes, AtomicEntry>;
};

export type NestedTableBranchProps = {
  depth?: number;
  isCollapsed: boolean;
  row: NonNullable<ReactNode>;
  path: string;
  color: string;
  value: NestedTableNodeTree[] | NonNullable<ReactNode>;
};

export type JsonLeafProps = {
  startDecoration?: ReactNode;
  inArray?: boolean;
  isLast?: boolean;
  label: ReactNode;
  value: ReactNode;
  type: NodeType;
};

export type NestedTableLeafProps = {
  value: NonNullable<ReactNode>;
  color: string;
  path: string;
  depth: number;
  isActive: boolean;
};

// Decompose + render methods
export interface JsonRenderProps {
  isLastElement: boolean;
  label: string;
  value: PossibleNodeTypes;
}

export interface NestedTableRenderProps {
  value: NestedTableNodeTree[] | NonNullable<ReactNode>;
  path: string;
  color: string;
  row: NonNullable<ReactNode>;
  isLeaf?: boolean;
}

export type RenderProps = Combine<JsonRenderProps, NestedTableRenderProps>;

export type RenderMethod = (props: RenderProps) => ReactElement;

export interface JsonDecomposeProps {
  src: NodeTrees | Exclude<PossibleNodeTypes, AtomicEntry>;
  sort: boolean;
  render: RenderMethod;
}

export interface NestedTableDecomposeProps {
  src: NestedTableNodeTree[];
  color: string;
  depth: number;
  render: RenderMethod;
}

export type DecomposeProps = Combine<JsonDecomposeProps, NestedTableDecomposeProps>;

export type DecomposeMethod = (props: DecomposeProps) => ReactElement | ReactElement[];
